import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  type CheckoutIntentPlan,
  getBillingActionErrorMessage,
  normalizeMarketingBullets,
  useCheckoutIntent,
  useCheckoutMutation,
  useCurrentSubscription,
  useTrialPlanSelectionMutation,
} from "../api/billing";
import {
  getCheckoutPlanKeyFromSearchParams,
} from "../lib/billingSelection";
import { trackTrialPlanSelected } from "../lib/analytics";

function formatPlanPrice(plan: Pick<CheckoutIntentPlan, "currency" | "price_monthly">) {
  if (plan.price_monthly == null) {
    return "Contact us";
  }
  return `${plan.currency ?? "AUD"} $${plan.price_monthly.toFixed(2)} / month`;
}

export default function BillingCheckoutConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planKey = getCheckoutPlanKeyFromSearchParams(searchParams);
  const [localError, setLocalError] = useState<string | null>(null);

  const checkoutIntent = useCheckoutIntent(planKey);
  const checkoutMutation = useCheckoutMutation();
  const currentSubscription = useCurrentSubscription();
  const trialPlanMutation = useTrialPlanSelectionMutation();

  const checkoutIntentData = checkoutIntent.data;
  const subscription = currentSubscription.data?.subscription ?? null;
  const plan = checkoutIntentData?.plan;
  const marketingBullets = normalizeMarketingBullets(plan?.marketing_bullets);
  const isTrialPlanSelection = subscription?.status === "trialing" || subscription?.status === "paused";

  useEffect(() => {
    if (checkoutIntentData?.eligibility_status === "already_subscribed" && !isTrialPlanSelection) {
      navigate("/billing?billing_state=already_subscribed", { replace: true });
    }
  }, [checkoutIntentData?.eligibility_status, isTrialPlanSelection, navigate]);

  const priceLabel = useMemo(() => {
    if (!plan || plan.price_monthly == null) return null;
    return formatPlanPrice(plan);
  }, [plan]);

  const handleContinue = () => {
    if (!plan?.plan_id) {
      setLocalError("This plan is not available for checkout.");
      return;
    }
    setLocalError(null);
    if (isTrialPlanSelection) {
      trialPlanMutation.mutate(
        { plan_id: plan.plan_id },
        {
          onSuccess: (data) => {
            trackTrialPlanSelected({
              trial_plan_id: subscription?.trial_plan_id,
              trial_plan_key: subscription?.trial_plan_key,
              selected_plan_id: data.subscription.selected_post_trial_plan.plan_id,
              selected_plan_key: data.subscription.selected_post_trial_plan.plan_key,
              payment_method_status: data.subscription.payment_method_status,
            });
            navigate("/billing?billing_state=trial_plan_selected");
          },
        }
      );
      return;
    }
    checkoutMutation.mutate({ plan_id: plan.plan_id });
  };

  const handleChooseDifferentPlan = () => {
    navigate(-1);
  };

  if (checkoutIntent.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (checkoutIntent.isError || checkoutIntentData?.eligibility_status === "invalid_plan" || !plan) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", px: 3, py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {checkoutIntentData?.message ?? "We could not find a selected plan. Go back and choose a plan before continuing to checkout."}
        </Alert>
        <Button variant="contained" onClick={handleChooseDifferentPlan}>
          Choose a different plan
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: 3, py: 6 }}>
      <Typography variant="h2" gutterBottom>
        Review your plan
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {isTrialPlanSelection
          ? "Confirm the paid plan you want after your trial. You will not be charged today."
          : "Confirm your selection before continuing to secure checkout."}
      </Typography>

      {checkoutIntentData?.eligibility_status === "retry_incomplete" && checkoutIntentData.message && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {checkoutIntentData.message}
        </Alert>
      )}

      {isTrialPlanSelection && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Confirm this as your paid plan after the trial. You will not be charged today.
        </Alert>
      )}

      {localError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {localError}
        </Alert>
      )}

      {trialPlanMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {getBillingActionErrorMessage(trialPlanMutation.error)}
        </Alert>
      )}

      <Stack spacing={3}>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected plan
              </Typography>
              <Typography variant="h3">{plan.display_name}</Typography>
            </Box>

            {priceLabel && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Price
                </Typography>
                <Typography variant="h5">{priceLabel}</Typography>
              </Box>
            )}

            {plan.description && (
              <Typography variant="body1" color="text.secondary">
                {plan.description}
              </Typography>
            )}

            {marketingBullets.length > 0 && (
              <>
                <Divider />
                <Stack spacing={1}>
                  {marketingBullets.map((bullet) => (
                    <Box key={bullet} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <CheckCircleOutlineRoundedIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                      <Typography variant="body2">{bullet}</Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={checkoutMutation.isPending || trialPlanMutation.isPending}
          >
            {checkoutMutation.isPending || trialPlanMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : isTrialPlanSelection ? (
              "Use after trial"
            ) : (
              "Continue to secure checkout"
            )}
          </Button>
          <Button variant="text" onClick={handleChooseDifferentPlan}>
            Choose a different plan
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
